import {
	cn,
	convertCamelToSnakeCaseWithoutTableName,
	getTranslatedMessage,
} from '@/utils';

import type { AnyObject } from 'antd/es/_util/type';
import { DefaultOptionType } from 'antd/es/select';
import { SorterResult } from 'antd/es/table/interface';

import { TFunction } from 'i18next';
import { isNilOrEmpty } from 'ramda-adjunct';

import Tooltip from '../tooltip';
import { TTabItem } from './components/setting-column';
import { ColumnTypes, DataType, EditableCellType } from './type';

export const franchiseColumns: ColumnTypes = [
	{
		title: 'Name',
		dataIndex: 'franchiseName',
		key: 'franchiseName',
	},
	{
		title: 'Age',
		dataIndex: 'age',
		key: 'age',
	},
	{
		title: 'Address',
		dataIndex: 'address',
		key: 'address',
		editable: true,
		cellType: EditableCellType.TEXT_INPUT,
	},
];

export const applicantColumns: ColumnTypes = [
	{
		title: 'Applicant Name',
		dataIndex: 'applicantName',
		key: 'applicantName',
		editable: true,
	},
	{
		title: 'Phone',
		dataIndex: 'phone',
		key: 'phone',
	},
	{
		title: 'PostalCode',
		dataIndex: 'postalCode',
		key: 'postalCode',
	},
];

export const groupColumn = franchiseColumns.concat(applicantColumns);

export const data: DataType[] = [
	{
		key: '1',
		franchiseName: 'John Brown',
		applicantName: 'John Brown',
		age: 32,
		address: 'New York No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '2',
		franchiseName: 'Jim Green',
		applicantName: 'Jim Green',
		age: 42,
		address: 'London No. 1 Lake Park ASDASD',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '3',
		franchiseName: 'Joe Black',
		applicantName: 'Joe Black',
		age: 53,
		address: 'Sidney No. 1 Lake Park 12313',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '4',
		franchiseName: 'John Brown',
		applicantName: 'John Brown',
		age: 32,
		address: 'New York No. 1 Lake Park 123213',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '5',
		franchiseName: 'Jim Green',
		applicantName: 'Jim Green',
		age: 77,
		address: 'London No. 1 Lake P234324ark',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '6',
		franchiseName: 'Joe Blac32432k',
		applicantName: 'Joe Blac32432k',
		age: 434,
		address: 'Sidney No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '7',
		franchiseName: 'John Brown',
		applicantName: 'John Brown',
		age: 133,
		address: 'New York No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '8',
		franchiseName: 'Jim Green',
		applicantName: 'Jim Green',
		age: 42,
		address: 'London No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '9',
		franchiseName: 'Joe Black',
		applicantName: 'Joe Black',
		age: 123123,
		address: 'Sidney No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '10',
		franchiseName: 'Joe Black',
		applicantName: 'Joe Black',
		age: 123123,
		address: 'Sidney No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
	{
		key: '11',
		franchiseName: 'Joe Black',
		applicantName: 'Joe Black',
		age: 123123,
		address: 'Sidney No. 1 Lake Park',
		phone: '123123123131',
		postalCode: 'asdasdoji4123',
	},
];

export const checkedOptionsList = groupColumn.map((item) => ({
	value: item.key as string,
	label: item.title as string,
}));

export const defaultCheckedOptionsList = franchiseColumns.map(
	(item) => item.key as string
);

export const franchiseOptionsList = franchiseColumns.map((item) => ({
	value: item.key as string,
	label: item.title as string,
}));

export const applicantOptionsList = applicantColumns.map((item) => ({
	value: item.key as string,
	label: item.title as string,
}));

export const tabItems: TTabItem[] = [
	{
		key: 'DN',
		label: 'Doanh Nghiep',
		options: franchiseOptionsList,
		checkedList: defaultCheckedOptionsList,
	},
	{
		key: 'NLD',
		label: 'Nguoi Lao Dong',
		options: applicantOptionsList,
		checkedList: [],
	},
];

export const getSortSql = (params: { sorter: SorterResult<any> }) => {
	const { sorter } = params;

	const objectName = (sorter?.column as any)?.objectName;
	const field = convertCamelToSnakeCaseWithoutTableName(
		((sorter?.column as any)?.sortKey || sorter.field) as string,
		objectName,
		true
	);

	const sortSql = sorter?.order
		? `${objectName}.${field}.${sorter.order === 'descend' ? 'DESC' : 'ASC'}`
		: '';
	return sortSql;
};

export const getRenderTextInput = (
	value: any,
	record: AnyObject,
	index: number,
	column: ColumnTypes[number]
) => {
	if (isNilOrEmpty(value) || value === '-') return '-';

	const isTextArea = column?.cellType === EditableCellType.TEXTAREA;

	const title = isTextArea ? (
		<div className="block whitespace-pre-wrap">{value}</div>
	) : (
		value
	);
	return (
		<Tooltip title={title} destroyTooltipOnHide>
			<div
				className={cn(
					'h-auto 2xl:h-[15.5px] max-w-[200px] 2xl:max-w-[300px] 2xl:truncate mx-auto',
					!isTextArea && '2xl:whitespace-pre'
				)}
			>
				{value}
			</div>
		</Tooltip>
	);
};

export const getRenderDropdown = (
	value: any,
	record: AnyObject,
	index: number,
	column: ColumnTypes[number],
	t?: TFunction<'common', undefined>
) => {
	const { dropdownProps } = column as ColumnTypes[number];
	if (!dropdownProps?.required && isNilOrEmpty(value)) return '-';

	const options = dropdownProps?.optionsByRecord
		? dropdownProps?.optionsByRecord(record)
		: dropdownProps?.options;
	const option = options?.find((opt: DefaultOptionType) => opt.value === value);
	let label = option ? option.label : '-';

	label = t ? getTranslatedMessage(t, label) : label;
	if (label === '-') return label;
	return (
		<Tooltip title={label} destroyTooltipOnHide>
			<div className="h-[15.5px] max-w-[300px] truncate mx-auto">{label}</div>
		</Tooltip>
	);
};
